import { useEffect } from "react";

/**
 * Imperatively upsert per-route SEO meta into <head>.
 *
 * `react-helmet-async@2` has known issues with React 18 concurrent rendering
 * (see staylor/react-helmet-async#171) — declared <Helmet> children don't
 * always reach the DOM. This hook sidesteps the whole reconciliation dance
 * and just writes to `document.head` directly.
 *
 * Every call sets the passed values and removes any tags this hook added
 * previously, so navigating between routes always starts from a clean slate.
 *
 * Accepts:
 *   title, description, keywords, ogTitle, ogDescription, ogUrl, ogType,
 *   articleTags (string[]), schema (object → serialized to JSON-LD)
 */
const MANAGED_ATTR = "data-managed-meta";

const setMetaTag = (attr, key, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED_ATTR, "true");
    document.head.appendChild(el);
  } else {
    el.setAttribute(MANAGED_ATTR, "true");
  }
  el.setAttribute("content", value);
};

const clearManagedTags = () => {
  document.head
    .querySelectorAll(`[${MANAGED_ATTR}="true"]`)
    .forEach((el) => el.parentNode?.removeChild(el));
};

export const useDocumentMeta = ({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogUrl,
  ogType = "article",
  ogImage = "https://letspickquick.com/og-image.png",
  canonical,
  robots,
  articleTags = [],
  schema = null,
}) => {
  useEffect(() => {
    // Remove any tags we added on previous mount before writing new ones
    clearManagedTags();

    if (title) document.title = title;

    setMetaTag("name", "description", description);
    setMetaTag("name", "keywords", keywords);
    setMetaTag("name", "robots", robots);
    setMetaTag("property", "og:title", ogTitle || title);
    setMetaTag("property", "og:description", ogDescription || description);
    setMetaTag("property", "og:type", ogType);
    // Fall back to canonical when caller doesn't pass ogUrl explicitly, so
    // every route always has a self-referencing og:url.
    setMetaTag("property", "og:url", ogUrl || canonical);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:image:width", "1200");
    setMetaTag("property", "og:image:height", "630");
    setMetaTag("property", "og:image:alt", ogTitle || title);
    setMetaTag("property", "og:site_name", "PickQuick");
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", ogTitle || title);
    setMetaTag("name", "twitter:description", ogDescription || description);
    setMetaTag("name", "twitter:image", ogImage);
    setMetaTag("name", "twitter:image:alt", ogTitle || title);
    setMetaTag("name", "twitter:url", ogUrl || canonical);

    // Self-referencing canonical link.
    if (canonical) {
      // Remove any existing canonical (managed or not) to avoid duplicates.
      document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
      const link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", canonical);
      link.setAttribute(MANAGED_ATTR, "true");
      document.head.appendChild(link);
    }

    // article:tag can appear multiple times
    articleTags.forEach((tag) => {
      const el = document.createElement("meta");
      el.setAttribute("property", "article:tag");
      el.setAttribute("content", tag);
      el.setAttribute(MANAGED_ATTR, "true");
      document.head.appendChild(el);
    });

    // JSON-LD structured data (accepts one object or an array of objects)
    if (schema) {
      const arr = Array.isArray(schema) ? schema : [schema];
      arr.forEach((s) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(s);
        script.setAttribute(MANAGED_ATTR, "true");
        document.head.appendChild(script);
      });
    }

    return () => {
      clearManagedTags();
    };
  }, [
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogUrl,
    ogType,
    ogImage,
    canonical,
    robots,
    // Stable-serialize arrays/objects so effect only re-runs on real change
    articleTags.join("|"),
    schema ? JSON.stringify(schema) : null,
  ]);
};
